import { useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import {
  REVENUECAT_REQUIRED_SCOPES,
  REVENUECAT_REQUIRED_SCOPE_SECTIONS,
} from './revenuecatScopes';

/**
 * "How to get your API key" for the RevenueCat Connect dialog.
 *
 * 🔴 The wording is shared, word for word, with mvp-frontend's own Connect
 * RevenueCat dialog (VIBE-2147). Change both or neither.
 *
 * The three section names are what the user hunts for on RevenueCat's key
 * screen, so they carry the weight; the nine exact scope strings are reference
 * material behind a toggle, collapsed on every open, because matching nine
 * lines one by one is what made the earlier version unreadable.
 */
export default function RevenueCatHelp() {
  const [scopesOpen, setScopesOpen] = useState(false);

  return (
    <div className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
      <p className="flex items-center gap-2 font-medium text-foreground">
        <Info className="size-4" />
        How to get your API key
      </p>

      <ol className="mt-2 space-y-1.5">
        <li>
          1. In RevenueCat, open{' '}
          <strong className="text-foreground">Project settings → API keys</strong> and click{' '}
          <strong className="text-foreground">+ New</strong>.
        </li>
        <li>
          2. Choose <strong className="text-foreground">V2</strong>, then set these three sections
          to <strong className="text-foreground">Read only</strong>:
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            {REVENUECAT_REQUIRED_SCOPE_SECTIONS.map((section) => (
              <li key={section.label}>
                <strong className="text-foreground">{section.label}</strong>: {section.covers}
              </li>
            ))}
          </ul>
          <p className="mt-1.5">
            Leave everything else on <strong className="text-foreground">No access</strong>. We
            never write to your RevenueCat.
          </p>
        </li>
        <li>
          3. Click <strong className="text-foreground">Generate</strong> and paste the key here (it
          starts with <span className="font-mono">sk_</span>).
        </li>
      </ol>

      <p className="mt-2">
        Project ID is optional. Leave it empty and we&apos;ll use the project the key belongs to.
      </p>

      <button
        type="button"
        onClick={() => setScopesOpen((open) => !open)}
        aria-expanded={scopesOpen}
        aria-controls="rc-exact-scopes"
        className="mt-2 flex items-center gap-1 hover:text-foreground"
      >
        {scopesOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        Show exact permissions
      </button>

      {scopesOpen && (
        /* One scope per line so a single one can be copied; break-all because a
           scope string has no spaces to wrap at and the dialog must not scroll
           sideways. No section headings here — the sections are named above. */
        <ul id="rc-exact-scopes" className="mt-1 space-y-0.5 pl-5 font-mono text-xs">
          {REVENUECAT_REQUIRED_SCOPES.map((scope) => (
            <li key={scope} className="break-all">
              {scope}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
